import { Component } from '@angular/core';
import { FormComponentLookup, FormEntryComponentClass } from '@ptl/forms';
import { PlanServiceResponse } from '../../../plan-selection/interfaces/plan-service-response.interface';
import { Product } from '../../../plan-selection/interfaces/product.interface';
import { MedicarePlansService } from '../../../plan-selection/plan-summary-list/plan-service-strategies/medicare-plans.service';
import { County } from '../../../shared/interfaces/zip-county-response.interface';
import { PurlFormState, SendReferralFormState } from '../../purl-form/interfaces/purl.interface';
import { patchSendReferralState, PURL_STATE } from '../../purl-form/reducer/purl.reducer';
import { timer } from 'rxjs';
import { debounce } from 'rxjs/operators';

@FormComponentLookup('view-plans-dropdown')
@Component({
    selector: 'app-view-plans-dropdown',
    templateUrl: './view-plans-dropdown.component.html',
    styleUrls: ['./view-plans-dropdown.component.scss'],
    standalone: false
})
export class ViewPlansDropdownComponent extends FormEntryComponentClass {

  formControlName: string;
  availablePlans = [];
  year: string;
  productGroup: string;
  county: County;
  
  selectStyle: string;

  noPlansError: boolean = false;
  isLoadingPlans: boolean = false;
  planSelectionTimeout: any;

  constructor(private medicarePlansService: MedicarePlansService) {
    super();
  }

  async ngAfterViewInit() {
    // TODO - if this needs used somewhere other than send referral, need a way to configure where these values will come from
    const sendReferralState: SendReferralFormState = (this.reducerUtilityService.getPayloadStates()[PURL_STATE] as PurlFormState).sendReferral;
    this.formControlName = this.entry.formControlAttributes.formControlName;
    this.year = sendReferralState.year;
    this.productGroup = sendReferralState.productGroup;
    this.county = sendReferralState.county;

    await this.updatePlansList();

    this.initTriggers();
    this.initializeUpdateSubscriptions();
  }

  async updatePlansList() {
    if (!this.year || !this.productGroup || !this.county) {
      this.form.controls[this.formControlName].setValue(null);
      this.availablePlans = [];
      return;
    }

    // TODO - when we start to add additional product groups we will need to use different plans service calls
    this.noPlansError = false;
    this.isLoadingPlans = true;
    const payload = this.medicarePlansService.createPayload(this.productGroup, this.year, this.county);
    const plansResponse: PlanServiceResponse = await this.medicarePlansService.getPlans(payload);
    this.availablePlans = this.convertObjectListToArray(plansResponse.plans[this.productGroup]);
    this.isLoadingPlans = false;
    this.resetPlanSelectionIfNotAvailable();

    if (!this.availablePlans?.length) {
      this.noPlansError = true;
    }
  }

  resetPlanSelectionIfNotAvailable() {
    // Can't use form control values here because the form values won't update (race condition) before this value is checked
    let currentValue = this.reducerUtilityService.getStateFromPath(this.reducerUtilityService.getPayloadStates(), this.getFullStateDotPath());
    if (!this.availablePlans.filter((plan) => plan.productId === this.form.controls[this.formControlName].value?.productId).length){
      // Reset both the form control and the state value to completely wipe the value
      currentValue = null;
      this.form.controls[this.formControlName].setValue(null);
    }
  }

  // Update the plans list if any of these key values have changed appropriately
  // The debounce here mitigates all the changes that happen when the forms first load
  // Values are set/unset and these were triggering with empty values even though the
  // actual value would be correct after everything has loaded
  initializeUpdateSubscriptions() {
    this.form.controls.year.valueChanges.pipe(debounce(() => timer(500))).subscribe((year) => {
      if (this.year !== year) {
        this.year = year;
        this.updatePlansList();
      }
    });
    this.form.controls.productGroup.valueChanges.pipe(debounce(() => timer(500))).subscribe((group) => {
      if (group && this.productGroup !== group) {
        this.productGroup = group;
        this.updatePlansList();
      }
    });
    this.form.controls.county.valueChanges.pipe(debounce(() => timer(500))).subscribe((county) => {
      if (this.county?.county !== county?.county) {
        this.county = county;
        this.updatePlansList();
      }
    });
  }

  trackPlanComparison(option: Product<any, any>, value: Product<any, any>) {
    return option?.productId === value?.productId;
  }

  convertObjectListToArray(objectList): Product<any, any>[] {
    // minimum width for form fields is 350px, each character is given 10px of space multiplied at the end
    let maxLength = 35; 
    let results = [];

    for (let key in objectList) {
      if (objectList.hasOwnProperty(key)) {
        results.push(objectList[key]);

        // Determine the longest plan name so we can adjust the dropdown to not cut off any text
        if (objectList[key].productName.length > maxLength) {
          maxLength = objectList[key].productName.length;
        }
      }
    }

    // Set the width to 10px multiplied by the longest plan name available
    this.selectStyle = `min-width: ${maxLength * 10}px`;

    return results;
  }
}
